/* =============================================================================
   Store Locator — runtime tweaks for the Promap (RosePerl) app
   - Strip app's inline styles that override our CSS
   - Hide the gmp-place-autocomplete Web Component, show the fallback input
   - Custom always-visible scrollbar for the location list
   ========================================================================== */
(function () {
  'use strict';

  const SELECTOR_APP_BLOCK = '#locator-app-block';
  const STRIP_INLINE_TARGETS = [
    '#scasl-tab-radius',
    '#bh-sl-map-container',
    '#bh-sl-address',
  ];

  // ---------- 1. Strip the app's inline styles ----------
  function stripInline(node) {
    if (!node) return;
    node.removeAttribute('style');
  }

  function cleanupInlineStyles(root) {
    if (!root) return;
    STRIP_INLINE_TARGETS.forEach((sel) => {
      root.querySelectorAll(sel).forEach(stripInline);
    });
  }

  // ---------- 2. Kill focus-ring inside ALL nested shadow DOMs ----------
  const FOCUS_KILL_CSS = `
    .focus-ring,
    [class*="focus-ring"],
    [class*="focus_ring"],
    [class*="focusRing"],
    [part*="focus-ring"] {
      border-color: transparent !important;
      border: none !important;
      outline: none !important;
      opacity: 0 !important;
      display: none !important;
    }
  `;

  function injectIntoShadowRoot(shadowRoot) {
    if (!shadowRoot) return;
    if (shadowRoot.querySelector('style[data-focus-killer]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-focus-killer', '');
    style.textContent = FOCUS_KILL_CSS;
    shadowRoot.appendChild(style);
  }

  function walkShadowRoots(node) {
    if (!node) return;
    if (node.shadowRoot) {
      injectIntoShadowRoot(node.shadowRoot);
      Array.from(node.shadowRoot.querySelectorAll('*')).forEach(walkShadowRoots);
    }
    if (node.children) {
      Array.from(node.children).forEach(walkShadowRoots);
    }
  }

  function killShadowFocusRing(root) {
    walkShadowRoots(root);
  }

  // ---------- 3. Custom always-visible scrollbar ----------
  function installScrollbar(root) {
    if (!root) return;
    const panel = root.querySelector('.bh-sl-loc-list.scasl-location-list');
    const list = panel && panel.querySelector('.list');
    if (!panel || !list || panel.dataset.customScrollInstalled === '1') return;

    // Hide native scrollbar by giving the list extra right padding and clipping it
    list.style.scrollbarWidth = 'none';

    const track = document.createElement('div');
    track.className = 'w-main-store-locator__scrolltrack';
    const thumb = document.createElement('div');
    thumb.className = 'w-main-store-locator__scrollthumb';
    track.appendChild(thumb);
    panel.appendChild(track);

    function updateThumb() {
      const ratio = list.clientHeight / list.scrollHeight;
      if (ratio >= 1) {
        track.style.display = 'none';
        return;
      }
      track.style.display = '';
      const thumbHeight = Math.max(40, list.clientHeight * ratio);
      const maxScroll = list.scrollHeight - list.clientHeight;
      const maxThumbTop = list.clientHeight - thumbHeight;
      const top = (list.scrollTop / maxScroll) * maxThumbTop;
      thumb.style.height = thumbHeight + 'px';
      thumb.style.transform = 'translateY(' + top + 'px)';
    }

    list.addEventListener('scroll', updateThumb, { passive: true });
    window.addEventListener('resize', updateThumb);
    new MutationObserver(updateThumb).observe(list, { childList: true, subtree: true });
    requestAnimationFrame(updateThumb);

    // Allow dragging the thumb
    let dragging = false;
    let dragStartY = 0;
    let dragStartScrollTop = 0;

    thumb.addEventListener('mousedown', (e) => {
      dragging = true;
      dragStartY = e.clientY;
      dragStartScrollTop = list.scrollTop;
      document.body.style.userSelect = 'none';
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      const dy = e.clientY - dragStartY;
      const ratio = list.scrollHeight / list.clientHeight;
      list.scrollTop = dragStartScrollTop + dy * ratio;
    });

    document.addEventListener('mouseup', () => {
      dragging = false;
      document.body.style.userSelect = '';
    });

    // Click track to jump scroll
    track.addEventListener('click', (e) => {
      if (e.target === thumb) return;
      const rect = track.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const ratio = list.scrollHeight / list.clientHeight;
      list.scrollTop = (clickY / track.clientHeight) * list.scrollHeight - list.clientHeight / 2;
    });

    panel.dataset.customScrollInstalled = '1';
  }

  // ---------- Orchestration ----------
  function run() {
    const root = document.querySelector(SELECTOR_APP_BLOCK);
    if (!root) return;
    cleanupInlineStyles(root);
    killShadowFocusRing(root);
    installScrollbar(root);
  }

  // Initial run + retries (the app renders progressively)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  [100, 500, 1000, 2000, 4000].forEach((ms) => setTimeout(run, ms));

  // Watch for app DOM changes and re-apply tweaks
  const observer = new MutationObserver(() => run());
  const target = document.querySelector(SELECTOR_APP_BLOCK) || document.body;
  observer.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
})();
